import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import {useNavigate, useParams} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import {
    ArchiveOutlined,
    ArrowBackOutlined,
    DeleteOutlined,
    EditOutlined,
    ExpandLessOutlined,
    ExpandMoreOutlined,
    MailOutlineOutlined,
    SettingsOutlined,
    TableChartOutlined,
    ThumbDownOutlined,
    ThumbUpOutlined
} from '@material-ui/icons';
import PageNaoEncontrada from '../../../PageNaoEncontrada.jsx';
import PageWrapper from '../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../shared/pagetitle/PageTitle.js';
import Message from '../../../shared/message/Message.js';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import OutlinedButton from '../../../shared/buttons/OutlinedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import PainelDividido from '../../components/PainelDividido.jsx';
import ArvoreDocumentos from '../../components/inscricao/ArvoreDocumentos.jsx';
import VisualizadorDocumento from '../../components/inscricao/VisualizadorDocumento.jsx';
import StatusDocumentoInscricao from '../../components/inscricao/StatusDocumentoInscricao.jsx';
import {situacaoAvaliacao} from '../../components/inscricao/documentoInscricao.js';
import {
    descricaoStatusAvaliacao,
    localizarDocumento,
    manterSelecao,
    tituloDocumento,
    tituloPessoa
} from '../../components/inscricao/arvoreDocumentos.js';
import DialogEditarDocumentoAnexo from '../../dialogs/inscricao/DialogEditarDocumentoAnexo.jsx';
import DialogPendenciasDocumentos from '../../dialogs/inscricao/DialogPendenciasDocumentos.jsx';
import DialogEnviarEmailPendencias from '../../dialogs/inscricao/DialogEnviarEmailPendencias.jsx';
import DialogBaixarZipAprovados from '../../dialogs/inscricao/DialogBaixarZipAprovados.jsx';
import useCarregamentoPagina from '../../components/useCarregamentoPagina.js';
import useFetch from '../../../../hooks/useFetch.js';
import useCreate from '../../../../hooks/useCreate.js';
import useRemove from '../../../../hooks/useRemove.js';
import useCreateFile from '../../../../hooks/useCreateFile.js';
import useCheckModule from '../../../../hooks/useCheckModule.js';
import useDialogState from '../../../../hooks/useDialogState.js';
import {
    URL_AVALIACAO_DOCUMENTOS_AVALIACAO,
    URL_AVALIACAO_DOCUMENTOS_AVALIAR,
    URL_AVALIACAO_DOCUMENTOS_INSCRICAO,
    URL_AVALIACAO_DOCUMENTOS_PLANILHA,
    URL_AVALIACAO_DOCUMENTOS_TIPOS
} from '../../../../constants/index.js';
import {salvarArquivoResposta} from '../../../../utils/download.js';
import {backgroundMenu, containerPaper} from '../../../../utils/styles.js';

const PARAMS = {};
const CHAVE_LARGURA_ARVORE = 'sysbeneficio.avaliacaoDocumentos.larguraArvore';
const ID_DETALHES = 'avaliacao-documentos-detalhes';

const useStyles = makeStyles(theme => ({
    ...backgroundMenu(theme),
    ...containerPaper(theme),
    barraAcoes: {
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(3)
    },
    // Lado a lado, a área ocupa a altura da tela: a árvore rola sozinha e o PDF usa tudo o que sobra.
    areaTrabalho: {
        [theme.breakpoints.up('md')]: {
            height: 'calc(100vh - 32px)',
            minHeight: 560
        }
    },
    painelArvore: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto'
    },
    painelDocumento: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        // Tela baixa com os detalhes abertos: o painel rola em vez de vazar.
        overflowY: 'auto',
        padding: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            minHeight: '80vh'
        }
    },
    barraDocumento: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5)
    },
    tituloDocumento: {
        flex: 1,
        minWidth: 0,
        fontWeight: 500,
        color: theme.palette.primary.main
    },
    botaoDetalhes: {
        flexShrink: 0
    },
    detalhes: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(2),
        marginBottom: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    semSelecao: {
        color: theme.palette.secondary.dark,
        fontStyle: 'italic'
    },
    acoesDocumento: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(3)
    }
}));

/*
 * Avaliação de Documentos de uma inscrição (migrada do PrimeFaces): árvore
 * Avaliados / Negados / Sem avaliação, visualizador do arquivo e as ações de
 * aceitar, negar, remover avaliação e editar o documento. As pendências são
 * gerenciadas em diálogos, sem sair da tela (como no PrimeFaces). Rota
 * /administrativo/inscricoes/:id/documentos.
 */
export default function PageAvaliacaoDocumentos() {
    const classes = useStyles();
    const {id} = useParams();
    const navigate = useNavigate();
    const checkModule = useCheckModule();
    const permitido = checkModule('INSCRICAO');
    const [buscar, carregando] = useFetch({returnInnerLoadingState: true});
    const [buscarTipos] = useFetch({returnInnerLoadingState: true});
    const create = useCreate();
    const remove = useRemove();
    const createFile = useCreateFile();
    const {createToast} = useToast();
    const confirmation = useConfirmationDialog();
    const {dialogState, openDialog, closeDialog} = useDialogState({editar: false, pendencias: false, email: false, zip: false});
    const [dados, setDados] = useState(null);
    const [tipos, setTipos] = useState([]);
    const [idSelecionado, setIdSelecionado] = useState(null);
    // Detalhes e avaliação começam recolhidos; o que o usuário escolher vale ao trocar de documento.
    const [detalhesAbertos, setDetalhesAbertos] = useState(false);
    // Loading da página pelo LoadingModal global.
    useCarregamentoPagina(carregando);

    // Recarrega a lista e mantém o documento selecionado quando ele ainda existe.
    const carregar = () => buscar(URL_AVALIACAO_DOCUMENTOS_INSCRICAO(id), PARAMS, resultado => {
        setDados(resultado);
        setIdSelecionado(anterior => manterSelecao(resultado.documentos, anterior));
    });

    useEffect(() => {
        if (!permitido) {
            return;
        }

        setDados(null);
        setIdSelecionado(null);
        carregar();
        buscarTipos(URL_AVALIACAO_DOCUMENTOS_TIPOS(id), PARAMS, setTipos);
    }, [id, permitido]);

    if (!permitido) {
        return <PageNaoEncontrada/>;
    }

    const documentos = dados && dados.documentos ? dados.documentos : [];
    const documentoSelecionado = localizarDocumento(documentos, idSelecionado);
    const avaliacao = documentoSelecionado ? documentoSelecionado.avaliacao : null;
    const titulo = dados && dados.inscricao
        ? `Avaliação de Documentos - ${dados.inscricao.id} / ${dados.inscricao.nome}`
        : `Avaliação de Documentos - ${id}`;

    const voltar = () => navigate('/administrativo/inscricoes');

    const avaliar = aprovado => () => {
        const mensagem = aprovado
            ? 'Tem certeza que deseja aceitar este documento?'
            : 'Tem certeza que deseja negar este documento? Será criada uma pendência para ele e a situação da inscrição mudará para Com Pendência.';

        confirmation(mensagem, () => create(
            URL_AVALIACAO_DOCUMENTOS_AVALIAR(documentoSelecionado.idInscricaoAnexo),
            {aprovado},
            () => {
                createToast({
                    severity: 'success',
                    message: aprovado
                        ? 'Documento aceito com sucesso!'
                        : 'Documento negado com sucesso! A pendência foi criada e a inscrição está Com Pendência.'
                });
                carregar();
            }));
    };

    const removerAvaliacao = () => confirmation('Tem certeza que deseja remover essa avaliação?', () => remove(
        URL_AVALIACAO_DOCUMENTOS_AVALIACAO(avaliacao.id),
        () => {
            createToast({severity: 'success', message: 'Avaliação removida com sucesso!'});
            carregar();
        }));

    const baixarExcel = () => createFile(
        URL_AVALIACAO_DOCUMENTOS_PLANILHA(id),
        {},
        response => salvarArquivoResposta(response, `documentos_inscricao_${id}.xls`));

    return (
        <>
            <PageWrapper>
                <PageTitle title={titulo}/>
                <Message type='info' marginBottom>
                    Os documentos estão organizados em pastas. Clique em um documento para visualizar o conteúdo sem baixar
                    o arquivo e abra "Detalhes e avaliação", acima do arquivo, para ver os dados e aceitá-lo ou negá-lo.
                    Arraste a divisória entre a lista e o arquivo para ajustar a largura. Em "Gerenciar Pendências" é
                    possível adicionar ou remover os documentos que o inscrito ainda precisa enviar.
                </Message>
                <Paper className={clsx(classes.backgroundMenu, classes.barraAcoes)} elevation={0}>
                    <OutlinedButton startIcon={<ArrowBackOutlined/>} onClick={voltar}>
                        Voltar
                    </OutlinedButton>
                    <ContainedButton startIcon={<SettingsOutlined/>} onClick={openDialog('pendencias')}>
                        Gerenciar Pendências
                    </ContainedButton>
                    <ContainedButton startIcon={<MailOutlineOutlined/>} onClick={openDialog('email')}>
                        Enviar e-mails pendências
                    </ContainedButton>
                    <ContainedButton startIcon={<ArchiveOutlined/>} onClick={openDialog('zip')}>
                        Baixar Doc. Aprovados Zip
                    </ContainedButton>
                    <ContainedButton startIcon={<TableChartOutlined/>} onClick={baixarExcel}>
                        Baixar Excel
                    </ContainedButton>
                </Paper>
                <PainelDividido
                    className={classes.areaTrabalho}
                    chaveArmazenamento={CHAVE_LARGURA_ARVORE}
                    esquerda={(
                        <Paper className={clsx(classes.containerPaper, classes.painelArvore)}>
                            <ArvoreDocumentos
                                documentos={documentos}
                                idSelecionado={idSelecionado}
                                onSelecionar={setIdSelecionado}/>
                        </Paper>
                    )}
                    direita={(
                        <Paper className={clsx(classes.containerPaper, classes.painelDocumento)}>
                            {documentoSelecionado ? (
                                <>
                                    <div className={classes.barraDocumento}>
                                        <Typography
                                            variant='subtitle1'
                                            component='h2'
                                            noWrap
                                            title={tituloDocumento(documentoSelecionado)}
                                            className={classes.tituloDocumento}>
                                            {tituloDocumento(documentoSelecionado)}
                                        </Typography>
                                        <StatusDocumentoInscricao situacao={situacaoAvaliacao(avaliacao ? avaliacao.status : null)}/>
                                        <TextButton
                                            className={classes.botaoDetalhes}
                                            endIcon={detalhesAbertos ? <ExpandLessOutlined/> : <ExpandMoreOutlined/>}
                                            aria-expanded={detalhesAbertos}
                                            aria-controls={ID_DETALHES}
                                            onClick={() => setDetalhesAbertos(aberto => !aberto)}>
                                            Detalhes e avaliação
                                        </TextButton>
                                    </div>
                                    <Collapse in={detalhesAbertos} id={ID_DETALHES}>
                                        <div className={classes.detalhes}>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} sm={6}>
                                                    <LabelValue label='Documento de' value={tituloPessoa(documentoSelecionado.pessoa)}/>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <LabelValue label='Arquivo' value={documentoSelecionado.nomeArquivo || '-'}/>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <LabelValue label='Descrição' value={documentoSelecionado.descricao || '-'}/>
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <LabelValue label='Status' value={descricaoStatusAvaliacao(avaliacao)}/>
                                                </Grid>
                                                {!!avaliacao && (
                                                    <Grid item xs={12} sm={3}>
                                                        <LabelValue label='Data' value={avaliacao.data || '-'}/>
                                                    </Grid>
                                                )}
                                            </Grid>
                                            <div className={classes.acoesDocumento}>
                                                {avaliacao ? (
                                                    <OutlinedButton startIcon={<DeleteOutlined/>} onClick={removerAvaliacao}>
                                                        Remover avaliação
                                                    </OutlinedButton>
                                                ) : (
                                                    <>
                                                        <OutlinedButton startIcon={<ThumbDownOutlined/>} onClick={avaliar(false)}>
                                                            Negar documento
                                                        </OutlinedButton>
                                                        <ContainedButton startIcon={<ThumbUpOutlined/>} onClick={avaliar(true)}>
                                                            Aceitar documento
                                                        </ContainedButton>
                                                    </>
                                                )}
                                                <OutlinedButton startIcon={<EditOutlined/>} onClick={openDialog('editar')}>
                                                    Editar documento
                                                </OutlinedButton>
                                            </div>
                                        </div>
                                    </Collapse>
                                    <VisualizadorDocumento
                                        idAnexo={documentoSelecionado.idInscricaoAnexo}
                                        mimetype={documentoSelecionado.mimetype}
                                        nomeArquivo={documentoSelecionado.nomeArquivo}/>
                                </>
                            ) : (
                                <Typography variant='body2' className={classes.semSelecao}>
                                    {dados && !documentos.length
                                        ? 'Esta inscrição não possui documentos anexados.'
                                        : 'Selecione um documento na lista para visualizá-lo e avaliá-lo.'}
                                </Typography>
                            )}
                        </Paper>
                    )}/>
            </PageWrapper>
            <DialogEditarDocumentoAnexo
                open={dialogState.editar}
                onClose={closeDialog('editar')}
                anexo={documentoSelecionado}
                tipos={tipos}
                onSalvo={carregar}/>
            <DialogPendenciasDocumentos
                open={dialogState.pendencias}
                onClose={closeDialog('pendencias')}
                idInscricao={id}
                inscricao={dados ? dados.inscricao : null}
                tipos={tipos}
                onSalvo={carregar}/>
            <DialogEnviarEmailPendencias
                open={dialogState.email}
                onClose={closeDialog('email')}
                idInscricao={id}/>
            <DialogBaixarZipAprovados
                open={dialogState.zip}
                onClose={closeDialog('zip')}
                idInscricao={id}/>
        </>
    );
}
